import { API } from "aws-amplify";
import { GRAPHQL_AUTH_MODE } from "@aws-amplify/api";

/**
 * Tracks per-user login / activity timestamps in the UserActivity table.
 *
 * One row per Cognito user, keyed by `sub` (used as `id`), so writes are a
 * plain upsert. Scalar fields are updated WITHOUT `_version` on purpose — with
 * AppSync conflict detection this AUTOMERGEs to the latest value per field,
 * which is exactly the "last writer wins" semantics we want for timestamps and
 * avoids any version bookkeeping.
 *
 * - recordLogin(session): called once on successful sign-in.
 * - touchActive(session): called on every route load (throttled below).
 * - userActivityListAll(): join source for the admin users table.
 */

// Minimum gap between "last active" writes. The feature updates on each route
// load, but this guard prevents hammering DynamoDB when a user clicks quickly
// through several pages in a row. Tune freely.
const ACTIVE_WRITE_THROTTLE_MS = 30 * 1000;

// Module-scoped so the throttle + existence hint persist across route changes
// (each PrivateRoute render gets a fresh hook instance otherwise).
let lastActiveWriteMs = 0;
let knownRowExists = false;

const getUserActivityQuery = /* GraphQL */ `
  query GetUserActivity($id: ID!) {
    getUserActivity(id: $id) {
      id
      sub
      email
      name
      lastLoginAt
      lastActiveAt
      loginCount
    }
  }
`;

const createUserActivityMutation = /* GraphQL */ `
  mutation CreateUserActivity($input: CreateUserActivityInput!) {
    createUserActivity(input: $input) {
      id
      lastLoginAt
      lastActiveAt
      loginCount
    }
  }
`;

const updateUserActivityMutation = /* GraphQL */ `
  mutation UpdateUserActivity($input: UpdateUserActivityInput!) {
    updateUserActivity(input: $input) {
      id
      lastLoginAt
      lastActiveAt
      loginCount
    }
  }
`;

const listUserActivitiesQuery = /* GraphQL */ `
  query ListUserActivities(
    $filter: ModelUserActivityFilterInput
    $limit: Int
    $nextToken: String
  ) {
    listUserActivities(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        id
        sub
        email
        name
        lastLoginAt
        lastActiveAt
        loginCount
      }
      nextToken
    }
  }
`;

const useUserActivity = () => {
  const authMode = (session: any) =>
    session
      ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
      : GRAPHQL_AUTH_MODE.AWS_IAM;

  const subOf = (session: any): string =>
    (session?.sub || session?.["cognito:username"] || "").toString().trim();

  async function getRow(id: string, session: any): Promise<any | null> {
    try {
      const res: any = await API.graphql({
        query: getUserActivityQuery,
        variables: { id },
        authMode: authMode(session),
      });
      return res?.data?.getUserActivity || null;
    } catch (e) {
      // Tolerate partial-error envelopes (data present alongside errors).
      if ((e as any)?.data?.getUserActivity) {
        return (e as any).data.getUserActivity;
      }
      return null;
    }
  }

  /** Record a successful login: bump lastLoginAt + loginCount, refresh identity. */
  async function recordLogin(session: any): Promise<void> {
    const id = subOf(session);
    if (!id) return;
    const nowIso = new Date().toISOString();
    const name = (session?.name || "").toString();
    const email = (session?.email || "").toString();

    try {
      const existing = await getRow(id, session);
      if (existing) {
        knownRowExists = true;
        await API.graphql({
          query: updateUserActivityMutation,
          variables: {
            input: {
              id,
              lastLoginAt: nowIso,
              lastActiveAt: nowIso,
              loginCount: (Number(existing.loginCount) || 0) + 1,
              ...(name ? { name } : {}),
              ...(email ? { email } : {}),
            },
          },
          authMode: authMode(session),
        });
      } else {
        await API.graphql({
          query: createUserActivityMutation,
          variables: {
            input: {
              id,
              sub: id,
              name,
              email,
              lastLoginAt: nowIso,
              lastActiveAt: nowIso,
              loginCount: 1,
              deleted: "0",
            },
          },
          authMode: authMode(session),
        });
        knownRowExists = true;
      }
      lastActiveWriteMs = Date.now();
    } catch (err) {
      // Non-fatal: never block login on activity tracking.
      console.warn("[useUserActivity] recordLogin failed", err);
    }
  }

  /** Refresh lastActiveAt (throttled). Safe to call on every route change. */
  async function touchActive(session: any): Promise<void> {
    const id = subOf(session);
    if (!id) return;
    const now = Date.now();
    if (now - lastActiveWriteMs < ACTIVE_WRITE_THROTTLE_MS) return;
    lastActiveWriteMs = now; // reserve the slot before the async write
    const nowIso = new Date().toISOString();

    try {
      if (knownRowExists) {
        await API.graphql({
          query: updateUserActivityMutation,
          variables: { input: { id, lastActiveAt: nowIso } },
          authMode: authMode(session),
        });
        return;
      }
      // Existence unknown (e.g. page reload without a fresh login) — upsert.
      const existing = await getRow(id, session);
      if (existing) {
        knownRowExists = true;
        await API.graphql({
          query: updateUserActivityMutation,
          variables: { input: { id, lastActiveAt: nowIso } },
          authMode: authMode(session),
        });
      } else {
        await API.graphql({
          query: createUserActivityMutation,
          variables: {
            input: {
              id,
              sub: id,
              name: (session?.name || "").toString(),
              email: (session?.email || "").toString(),
              lastActiveAt: nowIso,
              loginCount: 0,
              deleted: "0",
            },
          },
          authMode: authMode(session),
        });
        knownRowExists = true;
      }
    } catch (err) {
      console.warn("[useUserActivity] touchActive failed", err);
    }
  }

  /** All activity rows, keyed by sub — join source for the admin users list. */
  async function listAll(session: any): Promise<Record<string, any>> {
    const bySub: Record<string, any> = {};
    let nextToken: string | null = null;
    try {
      do {
        const res: any = await API.graphql({
          query: listUserActivitiesQuery,
          variables: { limit: 1000, nextToken },
          authMode: authMode(session),
        });
        const page = res?.data?.listUserActivities;
        for (const item of page?.items || []) {
          if (item && (item.sub || item.id)) {
            bySub[(item.sub || item.id).toString()] = item;
          }
        }
        nextToken = page?.nextToken || null;
      } while (nextToken);
    } catch (e) {
      // Tolerate partial-error envelopes.
      const page = (e as any)?.data?.listUserActivities;
      for (const item of page?.items || []) {
        if (item && (item.sub || item.id)) {
          bySub[(item.sub || item.id).toString()] = item;
        }
      }
      if (!page) console.warn("[useUserActivity] listAll failed", e);
    }
    return bySub;
  }

  return {
    userActivityRecordLogin: recordLogin,
    userActivityTouchActive: touchActive,
    userActivityListAll: listAll,
  };
};

export default useUserActivity;
